import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Container, Card, Table, Button, Spinner, Alert, Form, Badge, Modal } from 'react-bootstrap';
import { FaHome, FaSearch, FaEdit, FaSave, FaPlus } from 'react-icons/fa';
import { protectedResources, scopes } from "../authConfig";
import useFetchWithMsal from '../hooks/useFetchWithMsal';

export const EntryStatus = () => {
    const navigate = useNavigate();
    const { instance } = useMsal();
    const [entries, setEntries] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [entryToUpdate, setEntryToUpdate] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const { execute } = useFetchWithMsal({
        scopes: scopes.callapi
    });

    // Fetch seasons
    const fetchSeasons = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await execute(
                'GET', 
                `${protectedResources.getMasterdatas.endpoint}/season`
            );
            
            if (response && Array.isArray(response)) {
                setSeasons(response);
            } else {
                setSeasons([]);
                // Only set error message if it's not a 404 error
                if (response && response.message && response.status !== 404) {
                    setError(response.message);
                } else if (response && response.status === 404) {
                    // For 404 errors, set a generic message or handle silently
                    console.log("404 error: Resource not found");
                }
            }
        } catch (error) {
            console.error("Error fetching seasons:", error);
            setError("期情報の取得中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    }, [execute]);

    // Fetch company entries
    const fetchEntries = useCallback(async () => {
        if (!companyId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await execute(
                'GET', 
                `${protectedResources.getCompanyEntries.endpoint}/${companyId}?resolveRelations=true`
            );
            
            if (response && Array.isArray(response)) {
                setEntries(response);
            } else {
                setEntries([]);
                // Only set error message if it's not a 404 error
                if (response && response.message && response.status !== 404) {
                    setError(response.message);
                } else if (response && response.status === 404) {
                    // For 404 errors, set a generic message or handle silently
                    console.log("404 error: Resource not found");
                }
            }
        } catch (error) {
            console.error("Error fetching company entries:", error);
            setError("エントリー情報の取得中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    }, [companyId, execute]);

    // Create new entry
    const createNewEntry = async (seasonId) => {
        if (!companyId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const newEntry = {
                seasonId: seasonId,
                companyId: companyId,
                entryCount: 0,
                status: 'pending'
            };
            
            await execute(
                'POST', 
                `${protectedResources.createMasterData.endpoint}/entry`,
                newEntry
            );
            
            // Refresh entries after creation
            fetchEntries();
        } catch (error) {
            console.error("Error creating new entry:", error);
            setError("エントリー作成中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    };

    // Check if current date is within registration period for a specific entry
    const isWithinAcceptancePeriod = (entry) => {
        if ( !entry.acceptanceStartDate || !entry.acceptanceEndDate) {
            return false;
        }
        
        const today = new Date();
        const startDate = new Date(entry.acceptanceStartDate);
        const endDate = new Date(entry.acceptanceEndDate);
        
        // Set end date to end of the day
        endDate.setHours(23, 59, 59, 999);
        
        return today >= startDate && today <= endDate;
    };

    // Check if a season has an entry for the current company
    const hasEntryForSeason = (seasonId) => {
        return entries.some(entry => entry.seasonId === seasonId);
    };

    // Open modal for entry edit
    const openEditModal = (entry) => {
        setEntryToUpdate({
            ...entry,
            entryCount: entry.entryCount || 0
        });
        setShowModal(true);
    };

    // Handle entry count input change
    const handleEntryCountChange = (value) => {
        setEntryToUpdate(prev => ({
            ...prev,
            requestedEntryCount: value
        }));
    };

    // Handle entry count update
    const handleEntryCountUpdate = async () => {
        if (!entryToUpdate || !entryToUpdate.id) return;
        
        try {
            setLoading(true);
            
            // Following the pattern from MasterDataEditForm.jsx
            // Using the updateMasterData endpoint to update the entry
            const currentDate = new Date().toISOString();
            const updatedData = {
                ...entryToUpdate,
                requestedEntryCount: parseInt(entryToUpdate.requestedEntryCount),
                updatedDate: currentDate
            };
            
            await execute(
                'PUT',
                `${protectedResources.updateMasterData.endpoint}/entry`,
                updatedData
            );
            
            setShowModal(false);
            setEntryToUpdate(null);
            fetchEntries(); // Refresh entries after update
        } catch (error) {
            console.error("Error updating entry count:", error);
            setError("エントリー数の更新中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    };

    // Close modal and reset form
    const handleCloseModal = () => {
        setShowModal(false);
        setEntryToUpdate(null);
    };

    // Initialize component
    useEffect(() => {
        const activeAccount = instance?.getActiveAccount();
        
        if (!activeAccount) {
            setLoading(true);
            return;
        }
        
        if (activeAccount?.idTokenClaims?.extension_PermissionType !== 3) {
            navigate('/error');
            return;
        }
        
        const userCompanyId = activeAccount?.idTokenClaims?.extension_CompanyId;
        if (userCompanyId) {
            setCompanyId(userCompanyId);
            const userCompanyName = activeAccount?.idTokenClaims?.extension_CompanyName;
            if (userCompanyName) {
                setCompanyName(userCompanyName);
            }
        } else {
            setError("ユーザーに関連付けられた企業情報が見つかりません。");
            setLoading(false);
        }
    }, [instance, navigate]);

    useEffect(() => {
        if (companyId) {
            fetchEntries();
            fetchSeasons();
        }
    }, [companyId, fetchEntries, fetchSeasons]);

    return (
        <Container className="py-5">
            <Card className="border-0 shadow-sm mb-5">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                            <h1 className="fw-bold mb-0">エントリー状況一覧</h1>
                            {companyName && <p className="text-muted mb-0">{companyName}</p>}
                        </div>
                        <Button 
                            variant="outline-secondary" 
                            className="d-flex align-items-center gap-2" 
                            onClick={() => navigate('/hr-manager')}
                        >
                            <FaHome /> ホームへ戻る
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            {/* Season List Section */}
            <Card className="border-0 shadow-sm mb-5">
                <Card.Header className="bg-light py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">期一覧</h5>
                        <Button 
                            variant="outline-primary" 
                            className="d-flex align-items-center gap-2"
                            onClick={() => fetchSeasons()}
                            disabled={loading}
                        >
                            <FaSearch /> 更新
                        </Button>
                    </div>
                </Card.Header>
                
                <Card.Body>
                    {loading && seasons.length === 0 ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                            <p className="mt-2">期データを読み込み中...</p>
                        </div>
                    ) : seasons.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted">期データがありません。</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>期名</th>
                                        <th>開始日</th>
                                        <th>終了日</th>
                                        <th>エントリー受付開始日</th>
                                        <th>エントリー受付終了日</th>
                                        <th>ステータス</th>
                                        <th>操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {seasons.map((season) => {
                                        const hasEntry = hasEntryForSeason(season.id);
                                        const entryStatus = (() => {
                                            const today = new Date();
                                            const startDate = season.acceptanceStartDate ? new Date(season.acceptanceStartDate) : null;
                                            const endDate = season.acceptanceEndDate ? new Date(season.acceptanceEndDate) : null;
                                            
                                            if (!startDate || !endDate) return null;
                                            
                                            // Set end date to end of the day
                                            endDate.setHours(23, 59, 59, 999);
                                            
                                            if (today < startDate) return "受付前";
                                            if (today > endDate) return "受付終了";
                                            return "受付中";
                                        })();
                                        
                                        return (
                                            <tr key={season.id}>
                                                <td>{season.seasonName || '-'}</td>
                                                <td>{season.startDate || '-'}</td>
                                                <td>{season.endDate || '-'}</td>
                                                <td>{season.acceptanceStartDate || '-'}</td>
                                                <td>{season.acceptanceEndDate || '-'}</td>
                                                <td>
                                                    {hasEntry ? (
                                                        <Badge bg="success">登録済み</Badge>
                                                    ) : (
                                                        <Badge bg="warning" text="dark">未登録</Badge>
                                                    )}
                                                    {entryStatus && (
                                                        <Badge 
                                                            bg={entryStatus === "受付中" ? "info" : 
                                                               entryStatus === "受付前" ? "secondary" : "danger"} 
                                                            className="ms-2"
                                                        >
                                                            {entryStatus}
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    {!hasEntry && entryStatus === "受付中" && (
                                                        <Button 
                                                            variant="primary" 
                                                            size="sm"
                                                            onClick={() => createNewEntry(season.id)}
                                                            disabled={loading}
                                                        >
                                                            <FaPlus /> エントリー作成
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Existing Entries Section */}
            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-light py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <h5 className="mb-0">エントリー一覧</h5>
                            {loading && <Spinner animation="border" size="sm" />}
                        </div>
                        <Button 
                            variant="outline-primary" 
                            className="d-flex align-items-center gap-2"
                            onClick={() => fetchEntries()}
                            disabled={loading}
                        >
                            <FaSearch /> 更新
                        </Button>
                    </div>
                </Card.Header>
                
                <Card.Body>
                    {loading && entries.length === 0 ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                            <p className="mt-2">エントリーデータを読み込み中...</p>
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted">エントリーデータがありません。</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>期名</th>
                                        <th>エントリー枠</th>
                                        <th>リクエスト枠</th>
                                        <th>承認枠</th>
                                        <th>エントリー受付期間</th>
                                        <th>ステータス</th>
                                        <th>操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((entry) => {
                                        const canEdit = isWithinAcceptancePeriod(entry);
                                        return (
                                            <tr key={entry.id}>
                                                <td>{entry.seasonName || '-'}</td>
                                                <td>{entry.entryCount || 0}</td>
                                                <td>{entry.requestedEntryCount !== undefined ? entry.requestedEntryCount : (entry.entryCount || 0)}</td>
                                                <td>{entry.approvedEntryCount !== undefined ? entry.approvedEntryCount : (entry.status === 'approved' ? entry.entryCount : 0)}</td>
                                                <td>
                                                    {entry.acceptanceStartDate && entry.acceptanceEndDate ? (
                                                        <>
                                                            {entry.acceptanceStartDate} 〜 {entry.acceptanceEndDate}
                                                            {canEdit && (
                                                                <Badge bg="success" className="ms-2">受付中</Badge>
                                                            )}
                                                        </>
                                                    ) : (
                                                        '設定されていません'
                                                    )}
                                                </td>                                                <td>
                                                    {(entry.status && (
                                                        <Badge bg={
                                                            entry.status === 'approved' ? 'success' :
                                                            entry.status === 'rejected' ? 'danger' :
                                                            entry.status === 'pending' ? 'warning' : 'secondary'
                                                        } text={entry.status === 'pending' ? 'dark' : ''}>
                                                            {entry.status === 'approved' ? '承認済み' :
                                                             entry.status === 'rejected' ? '却下' :
                                                             entry.status === 'pending' ? '審査中' : entry.status}
                                                        </Badge>
                                                    )) || '-'}
                                                </td>
                                                <td>
                                                    <div className="d-flex justify-content-center gap-2">
                                                        {canEdit && entry.status === 'pending' ? (
                                                            <Button 
                                                                variant="primary" 
                                                                size="sm"
                                                                className="d-flex align-items-center gap-1"
                                                                onClick={() => openEditModal(entry)}
                                                                disabled={loading}
                                                            >
                                                                <FaEdit /> 編集
                                                            </Button>
                                                        ) : canEdit && entry.status === 'approved' ? (
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                className="d-flex align-items-center gap-1"
                                                                onClick={() => navigate(`/entry/${entry.id}/edit`)}
                                                            >
                                                                <FaEdit /> 参加者登録
                                                            </Button>
                                                        ) : entry.status === 'approved' ? (
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                className="d-flex align-items-center gap-1"
                                                                onClick={() => navigate(`/entry/${entry.id}/edit`)}
                                                                disabled={!canEdit}
                                                            >
                                                                <FaEdit /> 参加者登録
                                                            </Button>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Entry Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>エントリー数の編集</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {entryToUpdate && (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>期名</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={entryToUpdate.seasonName || '-'}
                                    disabled
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>エントリー枠数</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={entryToUpdate.entryCount || 0}
                                    disabled
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>リクエスト枠数</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={entryToUpdate.requestedEntryCount !== undefined ? entryToUpdate.requestedEntryCount : (entryToUpdate.entryCount || 0)}
                                    onChange={(e) => handleEntryCountChange(e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                    リクエスト枠数を入力してください
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>エントリー受付期間</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={
                                        entryToUpdate.acceptanceStartDate && entryToUpdate.acceptanceEndDate
                                            ? `${entryToUpdate.acceptanceStartDate} 〜 ${entryToUpdate.acceptanceEndDate}`
                                            : '設定されていません'
                                    }
                                    disabled
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>ステータス</Form.Label>
                                <div>
                                    {entryToUpdate.status && (
                                        <Badge bg={
                                            entryToUpdate.status === 'approved' ? 'success' :
                                            entryToUpdate.status === 'rejected' ? 'danger' :
                                            entryToUpdate.status === 'pending' ? 'warning' : 'secondary'
                                        } text={entryToUpdate.status === 'pending' ? 'dark' : ''}>
                                            {entryToUpdate.status === 'approved' ? '承認済み' :
                                             entryToUpdate.status === 'rejected' ? '却下' :
                                             entryToUpdate.status === 'pending' ? '審査中' : entryToUpdate.status}
                                        </Badge>
                                    )}
                                </div>
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        キャンセル
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleEntryCountUpdate}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                更新中...
                            </>
                        ) : (
                            <>
                                <FaSave className="me-1" /> 保存
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default EntryStatus;
